@if (simulation()) {
  <ng-template #ecoScore>
    @if (ecoScores(); as scores) {
      <span class="flex items-center gap-1.5">
        <app-ecoscore [ecoScore]="scores.before" />
        @if (scores.after !== scores.before) {
          <lucide-icon
            name="arrow-right"
            [size]="14"
            class="text-greyscale-500"
          />
          <app-ecoscore [ecoScore]="scores.after" />
        }
      </span>
    }
  </ng-template>

  <!-- A fold's header: its label, then "x/y" met, without → with the ticked
       tips, as the eco-score reads. -->
  <ng-template #foldHeader let-open let-label="label" let-count="count">
    <button
      type="button"
      class="flex w-full items-center justify-between gap-3 text-paragraph-s-reg"
      [attr.aria-expanded]="open()"
      (click)="open.set(!open())"
    >
      <span class="flex items-center gap-1.5 text-greyscale-600">
        <lucide-icon
          name="chevron-down"
          [size]="14"
          class="transition-transform"
          [class.rotate-180]="open()"
        />
        {{ label | transloco }}
      </span>
      <span class="flex shrink-0 items-center gap-1 tabular-nums">
        @if (count.after !== count.before) {
          <span class="text-greyscale-600">
            {{ count.before }}/{{ count.total }}
          </span>
          <lucide-icon
            name="arrow-right"
            [size]="14"
            class="text-greyscale-500"
          />
        }
        <span
          class="font-semibold"
          [class]="
            count.after > count.before
              ? 'text-validation-700'
              : 'text-greyscale-900'
          "
        >
          {{ count.after }}/{{ count.total }}
        </span>
      </span>
    </button>
  </ng-template>

  <!-- Objectives and commitments each fold to their count. Unfolded, every
       objective states its target, then its value without → with the ticked
       tips; values keep kg or t only, the per traveller, day or k€ being in
       the sentence. Every commitment shows its status, before → after when
       the ticked tips change it. -->
  <ng-template #goals>
    @if (objectiveRows().length) {
      <div class="flex flex-col gap-2">
        <ng-container
          [ngTemplateOutlet]="foldHeader"
          [ngTemplateOutletContext]="{
            $implicit: objectivesOpen,
            label: 'reductionTips.simulation.objectivesMet',
            count: objectiveCount(),
          }"
        />
        @if (objectivesOpen()) {
          <ul class="flex flex-col gap-2 pl-5">
            @for (row of objectiveRows(); track row.type) {
              <li class="flex flex-col gap-0.5">
                <span class="text-paragraph-xs-reg text-greyscale-800">
                  {{
                    row.targetLabelKey
                      | transloco: { target: formatCo2e.format(row.target) }
                  }}
                </span>
                <span
                  class="flex items-center gap-1.5 text-paragraph-s-reg tabular-nums"
                >
                  <span class="text-greyscale-600">
                    {{ formatCo2e.format(row.before) }}
                  </span>
                  <lucide-icon
                    name="arrow-right"
                    [size]="14"
                    class="text-greyscale-500"
                  />
                  <span
                    class="font-semibold"
                    [class]="
                      row.isReached
                        ? 'text-validation-700'
                        : 'text-greyscale-900'
                    "
                  >
                    {{ formatCo2e.format(row.after) }}
                  </span>
                  @if (row.isReached) {
                    <lucide-icon
                      name="check"
                      [size]="14"
                      class="text-validation-600"
                      [attr.aria-label]="
                        'reductionTips.simulation.objectiveReached' | transloco
                      "
                    />
                  }
                </span>
              </li>
            }
          </ul>
        }
      </div>
    }

    @if (commitmentCount().total) {
      <div class="flex flex-col gap-2">
        <ng-container
          [ngTemplateOutlet]="foldHeader"
          [ngTemplateOutletContext]="{
            $implicit: commitmentsOpen,
            label: 'reductionTips.simulation.commitmentsMet',
            count: commitmentCount(),
          }"
        />
        @if (commitmentsOpen()) {
          <ul class="flex flex-col gap-1.5 pl-5">
            @for (row of commitmentRows(); track row.action) {
              <li class="flex items-start justify-between gap-3">
                <span
                  class="min-w-0 text-paragraph-xs-reg"
                  [class]="
                    row.before === 'not_applicable'
                      ? 'text-greyscale-500'
                      : 'text-greyscale-800'
                  "
                >
                  {{
                    "objectives.reductionActions." + row.action + ".title"
                      | transloco
                  }}
                </span>
                <span class="flex shrink-0 items-center gap-1">
                  @if (row.after !== row.before) {
                    <span
                      class="flex h-5 w-5 items-center justify-center rounded-full"
                      [class]="statusStyle[row.before].badge"
                    >
                      <lucide-icon
                        [name]="statusStyle[row.before].icon"
                        [size]="12"
                      />
                    </span>
                    <lucide-icon
                      name="arrow-right"
                      [size]="12"
                      class="text-greyscale-500"
                    />
                  }
                  <span
                    class="flex h-5 w-5 items-center justify-center rounded-full"
                    [class]="statusStyle[row.after].badge"
                    [attr.aria-label]="
                      'reductionTips.goals.status.' + row.after | transloco
                    "
                  >
                    <lucide-icon
                      [name]="statusStyle[row.after].icon"
                      [size]="12"
                    />
                  </span>
                </span>
              </li>
            }
          </ul>
        }
      </div>
    }
  </ng-template>

  <ng-template #actions>
    @if (hasSelection()) {
      <p-button
        [label]="'reductionTips.simulation.apply' | transloco"
        size="small"
        [styleClass]="layout() === 'side' ? 'w-full' : ''"
        [disabled]="tipsService.isApplying()"
        [loading]="tipsService.isApplying()"
        (onClick)="tipsService.openApplyDialog()"
      />
      <p-button
        [label]="'reductionTips.simulation.cancel' | transloco"
        size="small"
        severity="secondary"
        [text]="true"
        [styleClass]="layout() === 'side' ? 'w-full' : ''"
        (onClick)="tipsService.clearSelection()"
      />
    } @else {
      <!-- Scrolls to the tips: a link, not a button with a chevron, which
           read as a dropdown about to open in place. -->
      <button
        type="button"
        class="flex items-center gap-1.5 text-paragraph-s-reg font-semibold text-primary-600 hover:underline"
        [class.justify-center]="layout() === 'side'"
        (click)="reveal.emit()"
      >
        {{ "reductionTips.simulation.seeTips" | transloco }}
        <lucide-icon name="arrow-down" [size]="14" />
      </button>
    }
  </ng-template>

  @if (layout() === "side" && collapsed()) {
    <!-- Folded: a slim tab on the report's edge, carrying what is ticked. -->
    <button
      type="button"
      class="flex flex-col items-center gap-3 rounded-lg border border-greyscale-200 bg-white px-2 py-4 text-primary-600 hover:bg-primary-50"
      [attr.aria-label]="'reductionTips.simulation.expand' | transloco"
      aria-expanded="false"
      (click)="collapsed.set(false)"
    >
      <lucide-icon name="chevrons-left" [size]="16" />
      <lucide-icon name="lightbulb" [size]="16" />
      <span
        class="text-paragraph-xs-reg font-semibold uppercase tracking-wider [writing-mode:vertical-rl]"
      >
        {{ "reductionTips.simulation.title" | transloco }}
      </span>
      @if (hasSelection()) {
        <span
          class="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1 text-paragraph-xs-reg font-semibold text-white"
        >
          {{ selectedCount() }}
        </span>
      }
    </button>
  } @else if (layout() === "side") {
    <section
      class="flex w-[300px] flex-col gap-4 rounded-lg border border-greyscale-200 bg-white p-5"
      [attr.aria-label]="'reductionTips.simulation.title' | transloco"
    >
      <div class="flex items-start justify-between gap-2">
        <h3
          class="flex flex-wrap items-center gap-x-2 text-paragraph-xs-reg font-semibold uppercase tracking-wider text-primary-600"
        >
          <lucide-icon name="lightbulb" [size]="14" />
          {{ "reductionTips.simulation.title" | transloco }}
          @if (hasSelection()) {
            <span
              class="font-medium normal-case tracking-normal text-greyscale-600"
            >
              ·
              {{
                "reductionTips.simulation.selected"
                  | transloco: { count: selectedCount() }
              }}
            </span>
          }
        </h3>
        <button
          type="button"
          class="-m-1 shrink-0 rounded p-1 text-greyscale-500 hover:bg-greyscale-100 hover:text-greyscale-800"
          [attr.aria-label]="'reductionTips.simulation.collapse' | transloco"
          aria-expanded="true"
          (click)="collapsed.set(true)"
        >
          <lucide-icon name="chevrons-right" [size]="16" />
        </button>
      </div>

      <p class="text-paragraph-s-reg text-greyscale-700">
        {{
          "reductionTips.simulation.hint"
            | transloco: { savings: co2e(displayPotential()) }
        }}
      </p>

      @if (hasSelection()) {
        <div class="flex justify-center">
          <app-chart-doughnut
            [data]="chartData()"
            [size]="chart().size"
            [thickness]="chart().thickness"
            [centerValue]="centerValue()"
            [centerLabel]="centerLabel()"
            textSize="medium"
            [showLabelInTooltip]="true"
          />
        </div>

        <dl class="flex flex-col gap-1.5 text-paragraph-s-reg">
          <div class="flex items-baseline justify-between gap-3">
            <dt class="text-greyscale-600">
              {{ "reductionTips.simulation.baseline" | transloco }}
            </dt>
            <dd class="tabular-nums text-greyscale-800">
              {{ co2e(displayBaseTotal()) }}
            </dd>
          </div>
          <div class="flex items-baseline justify-between gap-3">
            <dt class="text-greyscale-600">
              {{ "reductionTips.simulation.newTotal" | transloco }}
            </dt>
            <dd class="font-semibold tabular-nums text-primary-900">
              {{ co2e(displayNewTotal()) }}
            </dd>
          </div>
          <div class="flex items-baseline justify-between gap-3">
            <dt class="text-greyscale-600">
              {{ "reductionTips.simulation.avoided" | transloco }}
            </dt>
            <dd class="font-semibold tabular-nums text-validation-600">
              −{{ co2e(displayAvoided()) }} (−{{
                simulation()!.avoidedPercent
              }}%)
            </dd>
          </div>
        </dl>

        <div
          class="flex items-center justify-between gap-3 border-t border-greyscale-100 pt-3 text-paragraph-s-reg"
        >
          <span class="text-greyscale-600">{{
            "impactReport.ecoScore" | transloco
          }}</span>
          <ng-container [ngTemplateOutlet]="ecoScore" />
        </div>

        @if (hasGoals()) {
          <div class="flex flex-col gap-3 border-t border-greyscale-100 pt-3">
            <ng-container [ngTemplateOutlet]="goals" />
          </div>
        }
      }

      <div class="flex flex-col gap-1.5">
        <ng-container [ngTemplateOutlet]="actions" />
      </div>
    </section>
  } @else {
    <section
      class="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-greyscale-200 bg-white px-4 py-2.5 shadow-[0_-6px_18px_rgba(41,56,52,0.08)] md:px-6"
      [attr.aria-label]="'reductionTips.simulation.title' | transloco"
    >
      @if (hasSelection()) {
        <div class="flex min-w-0 items-center gap-3">
          <app-chart-doughnut
            [data]="chartData()"
            [size]="chart().size"
            [thickness]="chart().thickness"
            [showLabelInTooltip]="true"
          />
          <div class="flex min-w-0 flex-col tabular-nums">
            <span class="text-paragraph-s-reg text-greyscale-800">
              {{ co2e(displayBaseTotal()) }} →
              <span class="font-semibold text-primary-900">{{
                co2e(displayNewTotal())
              }}</span>
            </span>
            <span class="text-paragraph-xs-reg text-greyscale-600">
              <span class="font-semibold text-validation-600"
                >−{{ co2e(displayAvoided()) }} (−{{
                  simulation()!.avoidedPercent
                }}%)</span
              >
              ·
              {{
                "reductionTips.simulation.selected"
                  | transloco: { count: selectedCount() }
              }}
            </span>
          </div>
        </div>

        <div class="flex flex-col">
          <span class="text-paragraph-xs-reg text-greyscale-600">{{
            "impactReport.ecoScore" | transloco
          }}</span>
          <ng-container [ngTemplateOutlet]="ecoScore" />
        </div>

        @if (hasGoals()) {
          <div class="flex min-w-[220px] flex-col gap-1.5">
            <ng-container [ngTemplateOutlet]="goals" />
          </div>
        }
      } @else {
        <div class="flex min-w-0 items-center gap-2">
          <lucide-icon
            name="lightbulb"
            [size]="16"
            class="shrink-0 text-primary-600"
          />
          <p class="text-paragraph-s-reg text-greyscale-700">
            {{
              "reductionTips.simulation.hint"
                | transloco: { savings: co2e(displayPotential()) }
            }}
          </p>
        </div>
      }

      <div class="flex w-full gap-2 sm:ml-auto sm:w-auto">
        <ng-container [ngTemplateOutlet]="actions" />
      </div>
    </section>
  }
}
